import { faFileExcel, faSearch } from '@fortawesome/pro-regular-svg-icons';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Button from '../../components/Button';
import ContentBox from '../../components/FormArea/ContentBox';
import Dropdown from '../../components/Inputs/Dropdown';
import Input from '../../components/Inputs/Input';
import {
  MAX_DIMENSIONS,
  MAX_MEASURES,
  definitionProblems,
  normalizeDefinition,
} from '../../logic/statisticDefinition';

const MEASURES_FIELD = 'statistic-measures';
const DIMENSIONS_FIELD = 'statistic-dimensions';
const COLUMN_FIELD = 'statistic-column';

function toOptions(entries) {
  return (entries || []).map((entry) => ({
    value: entry.Key,
    label: entry.Label || entry.Key,
  }));
}

// react-select meldet eine leere Mehrfachauswahl je nach Aktion als null, []
// oder undefined.
function selectedKeys(values) {
  if (Array.isArray(values)) {
    return values;
  }
  return values ? [values] : [];
}

/**
 * Stellt eine Statistik aus dem Katalog zusammen (CARE-814). Kennzahlen und
 * Dimensionen kommen ausschliesslich aus dem Katalog des Servers; neue
 * Auswertungen brauchen deshalb hier keine Aenderung.
 */
class DefinitionForm extends Component {
  change = (patch) => {
    this.props.onChange(
      normalizeDefinition({ ...this.props.definition, ...patch }),
    );
  };

  limitReached = (selected, maximum) => (option) =>
    selected.length >= maximum && !selected.includes(option.value);

  submit = (handler) => {
    if (definitionProblems(this.props.definition).length === 0) {
      handler();
    }
  };

  renderColumnSelect(definition) {
    const dimensions = this.props.catalog.Dimensions || [];
    const options = toOptions(
      definition.DimensionKeys.map(
        (key) =>
          dimensions.find((entry) => entry.Key === key) || {
            Key: key,
            Label: key,
          },
      ),
    );

    if (options.length === 0) {
      return (
        <p className="Statistics-Note Input--fullWidth">
          Wählen Sie eine Dimension, um sie als Spalten einer Kreuztabelle
          darzustellen.
        </p>
      );
    }

    return (
      <Dropdown
        label="Als Spalten darstellen:"
        name={COLUMN_FIELD}
        inputId={COLUMN_FIELD}
        options={options}
        defaultValue={definition.ColumnDimensionKeys[0] || null}
        placeholder="Keine (einfache Tabelle)"
        isClearable
        getSelected={(value) =>
          this.change({ ColumnDimensionKeys: value ? [value] : [] })
        }
      />
    );
  }

  render() {
    const definition = normalizeDefinition(this.props.definition);
    const problems = definitionProblems(definition);
    const blocked = problems.length > 0;

    return (
      <ContentBox title="Auswertung zusammenstellen">
        <Dropdown
          label={`Kennzahlen (höchstens ${MAX_MEASURES}):`}
          name={MEASURES_FIELD}
          inputId={MEASURES_FIELD}
          isMulti
          options={toOptions(this.props.catalog.Measures)}
          defaultValue={definition.MeasureKeys}
          placeholder="Kennzahlen wählen"
          isOptionDisabled={this.limitReached(
            definition.MeasureKeys,
            MAX_MEASURES,
          )}
          getSelected={(values) =>
            this.change({ MeasureKeys: selectedKeys(values) })
          }
        />
        <Dropdown
          label={`Gruppieren nach (höchstens ${MAX_DIMENSIONS}):`}
          name={DIMENSIONS_FIELD}
          inputId={DIMENSIONS_FIELD}
          isMulti
          options={toOptions(this.props.catalog.Dimensions)}
          defaultValue={definition.DimensionKeys}
          placeholder="Keine Gruppierung (nur Gesamt)"
          isOptionDisabled={this.limitReached(
            definition.DimensionKeys,
            MAX_DIMENSIONS,
          )}
          getSelected={(values) =>
            this.change({ DimensionKeys: selectedKeys(values) })
          }
        />
        {this.renderColumnSelect(definition)}
        <Input
          type="date"
          label="Erstkontakt von:"
          name="statistic-from"
          defaultValue={definition.From || ''}
          onChange={(value) => this.change({ From: value })}
        />
        <Input
          type="date"
          label="Erstkontakt bis:"
          name="statistic-to"
          defaultValue={definition.To || ''}
          onChange={(value) => this.change({ To: value })}
        />
        <div className="Input--fullWidth" aria-live="polite">
          {blocked && (
            <ul className="Statistics-Hints">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="Input--fullWidth Buttonbar">
          <Button
            type="primary"
            icon={faSearch}
            disabled={blocked || this.props.evaluating}
            loading={this.props.evaluating}
            onClick={() => this.submit(this.props.onEvaluate)}
          >
            Auswerten
          </Button>
          <Button
            icon={faFileExcel}
            disabled={blocked || this.props.exporting}
            loading={this.props.exporting}
            onClick={() => this.submit(this.props.onExport)}
          >
            Excel exportieren
          </Button>
        </div>
      </ContentBox>
    );
  }
}

DefinitionForm.propTypes = {
  catalog: PropTypes.shape({
    Measures: PropTypes.arrayOf(
      PropTypes.shape({ Key: PropTypes.string, Label: PropTypes.string }),
    ),
    Dimensions: PropTypes.arrayOf(
      PropTypes.shape({ Key: PropTypes.string, Label: PropTypes.string }),
    ),
  }).isRequired,
  definition: PropTypes.shape({
    MeasureKeys: PropTypes.arrayOf(PropTypes.string),
    DimensionKeys: PropTypes.arrayOf(PropTypes.string),
    ColumnDimensionKeys: PropTypes.arrayOf(PropTypes.string),
    From: PropTypes.string,
    To: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onEvaluate: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  evaluating: PropTypes.bool,
  exporting: PropTypes.bool,
};

DefinitionForm.defaultProps = {
  evaluating: false,
  exporting: false,
};

export default DefinitionForm;
